import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import CloseIcon from "@mui/icons-material/Close";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import classes from "./FeatureRequest.module.css";
import TextField from "@mui/material/TextField";
import AsttButton from "components/AsttButton/AsttButton";
import ElectricBoltRoundedIcon from "@mui/icons-material/ElectricBoltRounded";
import { useLocation } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import SnackBar from "components/SnackBar/SnackBar";
import InfoModal from "components/CenterPoint/InfoModal/InfoModal";
import CheckCircleSharpIcon from "@mui/icons-material/CheckCircleSharp";
import { getAiPrompt, jiraFeatureRequest } from "services/userServices";
import applicationUsageLog from "../../utils/applicationUsagesLog";

export default function FeatureRequest(props) {
  const locationObj = useLocation();
  const { state, handleClose } = props;
  const [open, setOpen] = useState(false);
  const [featureType, setFeatureType] = useState("");
  const [comments, setComments] = useState("");

  const [loader, setLoader] = useState(false);
  const [error, setError] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const featureTypes = ["ASTT", "Centerpoint","IDM OG Ownership","LearnPoint", "OMT", "Others"];
  const handleChange = (event) => {
    setFeatureType(event.target.value);
  };
  const [infoModal, setInfoModal] = useState({
    state: false,
    header: "",
    message: "",
    icon: <CheckCircleSharpIcon className={classes.icon} />,
  });

  useEffect(() => {
    setOpen(state);
  }, [state]);

  const resetValues = () => {
    setError(false);
    setIsClicked(false);
    setComments("");
  };

  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  const handleInfoClose = () => {
    setInfoModal((prev) => {
      return {
        ...prev,
        state: false,
      };
    });
  };

  const errorHandler = (open, message, severity) => {
    setSnack({
      open: open,
      message: message,
      severity: severity,
    });
  };

  useEffect(() => {
    if (locationObj.pathname === "/centerpoint") setFeatureType("Centerpoint");
    else if (locationObj.pathname === "/learnpoint")
      setFeatureType("LearnPoint");
    else setFeatureType("ASTT");
  }, []);

  const handleCommentChange = (e) => {
    if (e.target.value.length <= 2000) setComments(e.target.value);
    if (e.target.value >= 100) {
      setError(false);
    }
  };

  const onSubmit = async () => {
    if (!featureType || !comments) setError(true);
    else {
      applicationUsageLog(
        localStorage.getItem("User"),
        localStorage.getItem("EmailId"),
        "ASTT",
        "Feature Request"
      );
      setError(false);

      let jiraSummary = "";
      let jiraAcceptanceCriteria = "";

      const aISummarypayload = {
        text: comments || "",
        mode: "Summarize",
        maxLength: 100,
      };

      const aIAcceptancepayload = {
        text: comments || "",
        mode: "Summarize",
        maxLength: 2000,
      };

      try {
        setLoader(true);
        setIsClicked(true);

        const responseSummary = await getAiPrompt(aISummarypayload);
        if (responseSummary?.message === "Success") {
          jiraSummary = responseSummary.data;
        } else if (responseSummary?.message === "Error") {
          errorHandler("Error in generating acceptance criteria", "error");
        }

        const responseAcceptance = await getAiPrompt(aIAcceptancepayload);
        if (responseAcceptance?.message === "Success") {
          jiraAcceptanceCriteria = responseAcceptance.data;
        } else if (responseAcceptance?.message === "Error") {
          errorHandler("Error in generating acceptance criteria", "error");
        }
        const response = await jiraFeatureRequest(
          featureType + ": " + jiraSummary,
          comments,
          jiraAcceptanceCriteria,
          localStorage.getItem("EmailId")
        );
        if (response?.message === "Success") {
          const parsedData = JSON.parse(response.data);

          setInfoModal((prev) => {
            return {
              ...prev,
              state: true,
              message:
                parsedData?.key +
                " has been created in the Jira board for analysis by the product team. Development will commence once the task receives approval.",
              header: "Success",
            };
          });
          handleClose();
          resetValues();
        } else errorHandler(true, "Feature could not be requested !", "error");
      } catch {
        errorHandler(true, "Something went wrong !", "error");
      } finally {
        setLoader(false);
      }
    }
  };

  return (
    <div>
      {loader ? (
        <div className={classes.loader}>
          <CircularProgress sx={{ color: "#A8A8A8" }} />
        </div>
      ) : null}
      <InfoModal
        open={infoModal.state}
        message={infoModal.message}
        header={infoModal.header}
        icon={infoModal.icon}
        handleClose={handleInfoClose}
      />
      <Modal
        open={open}
        onClose={() => {
          resetValues();
          handleClose();
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box className={classes.modalContainer}>
          <div className={classes.container}>
            <h2 className={classes.header}>
              <ElectricBoltRoundedIcon /> Feature Request
            </h2>
            <CloseIcon
              onClick={() => {
                resetValues();
                handleClose();
              }}
              className={classes.closeIcon}
            />
          </div>
          <Grid container spacing={2} className={classes.gridContainer}>
            <Grid item xs={12}>
              <h3 className={classes.heading}>User Details</h3>
            </Grid>
            <Grid item xs={6}>
              <p className={classes.label}>Employee Name</p>
            </Grid>
            <Grid item xs={6}>
              <p className={classes.label}>Employee Oracle ID</p>
            </Grid>
            <Grid item xs={6}>
              <p className={classes.info}>{localStorage.getItem("User")}</p>
            </Grid>
            <Grid item xs={6}>
              <p className={classes.info}>{localStorage.getItem("OracleId")}</p>
            </Grid>
          </Grid>
          <Grid container spacing={2} className={classes.issueContainer}>
            <Grid item xs={12}>
              <h3 className={classes.heading}>Feature Details</h3>
            </Grid>
            <Grid item xs={6}>
              <p className={classes.label}>Feature Type</p>
            </Grid>
            <Grid item xs={6}></Grid>
            <Grid item xs={6}>
              <FormControl fullWidth className={classes.paddingRight}>
                <InputLabel id="demo-simple-select-label">
                  Select Feature type
                </InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={featureType}
                  label=" Select Feature type"
                  onChange={handleChange}
                >
                  {featureTypes.map((item) => (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}></Grid>
            <Grid item xs={12}>
              <p className={classes.label}>Comment</p>
              <TextField
                id="outlined-multiline-flexible"
                placeholder="2000 Character Limit"
                multiline
                rows={4}
                value={comments}
                onChange={handleCommentChange}
                error={error}
                helperText={
                  <span
                    className={
                      comments.length > 0 && comments.length < 100
                        ? classes.helperText
                        : classes.helperTextValid
                    }
                  >
                    Please describe in at least 100 characters
                  </span>
                }
                className={classes.textField}
              />
            </Grid>
            {error && (
              <p className={classes.error}>
                Please enter all the fields before submiting !
              </p>
            )}
            <Grid item xs={12}>
              <AsttButton
                onClick={onSubmit}
                disabled={comments.length < 100 || isClicked}
              >
                Submit
              </AsttButton>
            </Grid>
          </Grid>
        </Box>
      </Modal>
      <SnackBar snack={snack} snackReset={snackReset}></SnackBar>
    </div>
  );
}
